export const IDLE_CONFIG = {
  movementThreshold: 3,
  idleThreshold: 0,
};

const OFFLINE_THRESHOLD_MS = 60 * 60 * 1000;

export const isStaleTimestamp = (
  timestamp: number | null | undefined,
): boolean => {
  if (!timestamp || timestamp <= 0) return true;
  const ms = timestamp > 9999999999 ? timestamp : timestamp * 1000;
  return Date.now() - ms > OFFLINE_THRESHOLD_MS;
};

export const timestampToMs = (
  timestamp: number | null | undefined,
): number => {
  if (!timestamp || timestamp <= 0) return Date.now();
  return timestamp > 9999999999 ? timestamp : timestamp * 1000;
};

export interface StatusResult {
  status: string;
  idleSince: number | null;
}

export const determineVehicleStatus = (
  speed: number | null | undefined,
  ignition: boolean | null | undefined,
  lastTimestamp: number | null | undefined,
  previousStatus?: string,
  movementDetected?: boolean,
  previousIdleSince?: number | null,
  idleSignal?: boolean | null,
): StatusResult => {
  if (!lastTimestamp || lastTimestamp <= 0) {
    const keep =
      previousStatus && previousStatus !== "offline"
        ? previousStatus
        : "offline";
    return {
      status: keep,
      idleSince:
        keep === "ralenti" || keep === "stopped"
          ? previousIdleSince ?? null
          : null,
    };
  }

  if (isStaleTimestamp(lastTimestamp)) {
    return { status: "offline", idleSince: null };
  }

  const isMoving =
    (speed != null && speed > IDLE_CONFIG.movementThreshold) ||
    movementDetected === true;

  if (isMoving) {
    return { status: "driving", idleSince: null };
  }

  if (idleSignal === true) {
    const nowMs = timestampToMs(lastTimestamp);
    return { status: "ralenti", idleSince: previousIdleSince ?? nowMs };
  }

  if (idleSignal === false) {
    return { status: "stopped", idleSince: null };
  }

  if (ignition === true) {
    // El tiempo en ralentí se mide con el reloj del dispositivo (timestamp del
    // reporte), no con Date.now(): así es correcto aunque el reloj del server
    // no coincida con el del device (o en replay de datos históricos).
    const nowMs = timestampToMs(lastTimestamp);
    const idleSince = previousIdleSince ?? nowMs;
    if (nowMs - idleSince >= IDLE_CONFIG.idleThreshold) {
      return { status: "ralenti", idleSince };
    }
    return { status: "stopped", idleSince };
  }

  if (ignition === false) {
    return { status: "stopped", idleSince: null };
  }

  if (speed != null) {
    return { status: "stopped", idleSince: null };
  }

  return {
    status:
      previousStatus && previousStatus !== "offline"
        ? previousStatus
        : "stopped",
    idleSince: null,
  };
};

export const odometerKmFromParams = (
  params: Record<string, unknown> | null | undefined,
): number | null => {
  if (!params) return null;
  const odoKm = params.odo_km ?? params.odometer_km;
  if (odoKm != null && !Number.isNaN(Number(odoKm))) return Number(odoKm);
  const meters = params.virtual_odometer ?? params.total_odometer;
  if (meters != null && !Number.isNaN(Number(meters))) return Number(meters) / 1000;
  return null;
};

// Δtiempo mínimo (ms) para confiar en una velocidad estimada. Por debajo, un
// avance chico (ruido de GPS/odómetro) se amplifica a km/h absurdos: p.ej. 10 m
// en 1 s daría 36 km/h. Reportes reales vienen con mucho más separación.
export const MIN_IMPLIED_SPEED_DELTA_MS = 3000;

export const impliedSpeedFromOdometer = (
  prevOdoKm: number | null | undefined,
  newOdoKm: number | null | undefined,
  prevTimestamp: number | null | undefined,
  newTimestamp: number | null | undefined,
): number | null => {
  if (prevOdoKm == null || newOdoKm == null) return null;
  if (!prevTimestamp || prevTimestamp <= 0) return null;
  if (!newTimestamp || newTimestamp <= 0) return null;
  const deltaKm = newOdoKm - prevOdoKm;
  if (deltaKm <= 0) return null;
  const deltaMs = timestampToMs(newTimestamp) - timestampToMs(prevTimestamp);
  if (deltaMs < MIN_IMPLIED_SPEED_DELTA_MS) return null;
  return deltaKm / (deltaMs / 3600000);
};

const haversineKm = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const impliedSpeedFromDistance = (
  prevLat: number | null | undefined,
  prevLon: number | null | undefined,
  newLat: number | null | undefined,
  newLon: number | null | undefined,
  prevTimestamp: number | null | undefined,
  newTimestamp: number | null | undefined,
): number | null => {
  if (prevLat == null || prevLon == null || newLat == null || newLon == null) {
    return null;
  }
  // (0,0) es glitch de GPS, no posición real: ni previa ni nueva son medibles.
  if (prevLat === 0 && prevLon === 0) return null;
  if (newLat === 0 && newLon === 0) return null;
  if (!prevTimestamp || prevTimestamp <= 0) return null;
  if (!newTimestamp || newTimestamp <= 0) return null;
  const deltaKm = haversineKm(prevLat, prevLon, newLat, newLon);
  if (deltaKm <= 0) return null;
  const deltaMs = timestampToMs(newTimestamp) - timestampToMs(prevTimestamp);
  if (deltaMs < MIN_IMPLIED_SPEED_DELTA_MS) return null;
  return deltaKm / (deltaMs / 3600000);
};

// Velocidad efectiva unificada: si llega speed real se usa; si no, se estima en
// km/h por avance de odómetro (Δodo/Δt) y como último recurso por desplazamiento
// GPS (distancia/Δt). Todo pasa luego por el mismo umbral de movimiento.
export const resolveEffectiveSpeed = (
  speed: number | null | undefined,
  prevOdoKm: number | null | undefined,
  newOdoKm: number | null | undefined,
  prevLat: number | null | undefined,
  prevLon: number | null | undefined,
  newLat: number | null | undefined,
  newLon: number | null | undefined,
  prevTimestamp: number | null | undefined,
  newTimestamp: number | null | undefined,
): number | null | undefined => {
  if (speed != null) return speed;
  const odo = impliedSpeedFromOdometer(
    prevOdoKm,
    newOdoKm,
    prevTimestamp,
    newTimestamp,
  );
  if (odo != null) return odo;
  const pos = impliedSpeedFromDistance(
    prevLat,
    prevLon,
    newLat,
    newLon,
    prevTimestamp,
    newTimestamp,
  );
  if (pos != null) return pos;
  return speed;
};
